import { AddCircle12Regular, AddCircle16Regular, AddCircle24Filled, BinRecycleRegular, DismissRegular, Edit12Regular, EditRegular, EyeOffRegular, TextBold24Regular, TextItalic24Regular, TextUnderline24Regular } from "@fluentui/react-icons";
import {
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  Button,
  Text,
  Option,
  Title1,
  Table,
  TableRow,
  TableCell,
  TableHeader,
  TableHeaderCell,
  Dropdown,
  Spinner,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip
} from "@fluentui/react-components";
import { FC, useEffect, useState } from "react";
import supportQuestionService from "../../services/supportQuestionService";
import fileService from "../../services/fileService";
import { SupportQuestion } from "../../models/supportQuestion";
import { News, NewsListItem, NewsTypes, NewsUpdateDTO } from "../../models/news";
import { deleteNews, getNewsByQuery, updateNews } from "../../services/newsService";
import CreateEditNews from "../dialogs/CreateEditNews";
import { set } from "react-hook-form";
import { useAppContext } from "../../AppContext";

type Props = {
  onClose: () => void;
  open: boolean;
};

const NewsDrawer: FC<Props> = ({onClose, open}) => {
  const [news, setNews] = useState<NewsListItem[]>([]);
  const [selectedNews, setSelectedNews] = useState<NewsListItem>();
  const [newsLoading, setNewsLoading] = useState<boolean>(false);
  const [newsDialogOpen, setNewsDialogOpen] = useState<boolean>(false);
  const [queryNewsType, setQueryNewsType] = useState<number>(1);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
  const [newsToDelete, setNewsToDelete] = useState<number | null>(null);

  const {featurePermissions} = useAppContext();


  const newsTypes = NewsTypes;

  useEffect(() => {
    if(open){
      fetchNews();
    }
  }, [open, queryNewsType]);

  const fetchNews = async () => {
    setNewsLoading(true);
    try{
      const newsData = await getNewsByQuery({pageSize: 1000, newsTypeID: queryNewsType});
      setNews(newsData.news);
    }
    catch(err){
      console.log(err);
    }
    finally{
      setNewsLoading(false);
    }
  }

  const onDeleteClick = async (newsID: number) => {
    setNewsLoading(true);
    try{
      await deleteNews(newsID);
      setNews(prevNews => prevNews.filter(n => n.newsID !== newsID));
    }
    catch(err){
      console.log(err);
    }
    finally{
      setNewsLoading(false);
    }
  };

  const onUnpublishNews = async (news: NewsListItem) => {
    setNewsLoading(true);
    let yesterday: Date = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    console.log(news);
    const newsToUpdate: NewsUpdateDTO = {
      NewsID: news.newsID,
      NewsTypeID: news.newsTypeID,
      Header: news.header,
      Subject: news.subject,
      Body: news.body,
      Highlighted: news.highlighted,
      PublishDate: news.publishDate ? news.publishDate : undefined,
      UnpublishDate: yesterday.toISOString(),
      DepartmentID: news.departmentID,
      ModuleID: news.moduleID,
    };
    try{
      await updateNews(newsToUpdate);
      fetchNews();
    }
    catch(err){
      console.log(err);
    }
    finally{
      setNewsLoading(false);
    }

  }

  return (
    <>
    <Drawer
      size="large"
      position="end"
      open={open}
      onOpenChange={(_event, data) => !data.open && onClose()}
      modalType="non-modal"
    >
      <DrawerHeader>
        <DrawerHeaderTitle>
          <div className="flex items-start justify-between w-full">
            <Title1>
              Skapa/Redigera nyheter
            </Title1>
            <Button size="small" icon={<DismissRegular />} shape="circular" onClick={onClose}>
            </Button>
          </div>
        </DrawerHeaderTitle>
      </DrawerHeader>
      <DrawerBody>
        <div className="flex gap-4 my-4">
          {featurePermissions?.some(fp => fp.action == 'Create news') && <Button disabled={newsLoading} onClick={() => setNewsDialogOpen(true)} appearance="primary" icon={<AddCircle24Filled />}>
            Skapa nyhet
          </Button>}
          <Dropdown
            disabled={newsLoading}
            placeholder="Filtrera efter ämne"
            value={newsTypes.find(nt => nt.id === queryNewsType)?.name || ''}
            onOptionSelect={(e, data) => {
              setQueryNewsType(Number(data.optionValue));
            }}
          >
            {newsTypes.map(nt => (
              <Option key={nt.id} value={nt.id.toString()}>{nt.name}</Option>
            ))}
          </Dropdown>
        </div>
        <Table size="small" aria-label="Nyheter tabell">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>
                <Text weight="semibold">
                  Rubrik
                </Text>
              </TableHeaderCell>
              <TableHeaderCell>
                <Text weight="semibold">
                  Ämne
                </Text>
              </TableHeaderCell>
              <TableHeaderCell>
                <Text weight="semibold">
                  Publicering
                </Text>
              </TableHeaderCell>
              <TableHeaderCell>
                <Text weight="semibold">
                  Åtgärder
                </Text>
              </TableHeaderCell>
            </TableRow>
          </TableHeader>
          {newsLoading && (
            <div className="flex justify-center items-center py-8">
              <Spinner label="Laddar nyheter..." />
            </div>
          )}
          {!newsLoading && news.map((n: NewsListItem) => (
            <TableRow key={n.newsID} className="group">
              <TableCell>
                {n.header}
              </TableCell>
              <TableCell>
                {n.subject}
              </TableCell>
              <TableCell>
                {n.publishDate ? new Date(n.publishDate).toLocaleDateString("sv-SE") : "-"}
              </TableCell>
              <TableCell>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-50">
                  { featurePermissions?.some(fp => fp.action == 'Edit news') && <Tooltip content="Redigera" relationship="label"><Button
                    appearance="primary"
                    shape="circular"
                    icon={<EditRegular />}
                    onClick={() => { setSelectedNews(n); setNewsDialogOpen(true); }} /></Tooltip>}
                  { featurePermissions?.some(fp => fp.action == 'Delete news') && <Tooltip content="Ta bort" relationship="label"><Button
                    shape="circular"
                    icon={<BinRecycleRegular />}
                    onClick={() => { setNewsToDelete(n.newsID); setDeleteConfirmOpen(true); }} /></Tooltip>}
                  <Tooltip content="Avpublicera" relationship="label"><Button
                    shape="circular"
                    icon={<EyeOffRegular />}
                    onClick={() => { onUnpublishNews(n) } } /></Tooltip>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </Table>
      </DrawerBody>
    </Drawer>
      <CreateEditNews
        isOpen={newsDialogOpen}
        onClose={() => { setNewsDialogOpen(false); setSelectedNews(undefined); }}
        news={selectedNews}
        onSuccess={() => fetchNews()}
      />

      <Dialog open={deleteConfirmOpen} onOpenChange={(_, data) => setDeleteConfirmOpen(data.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Bekräfta borttagning</DialogTitle>
            <DialogContent>
              <Text>Är du säker på att du vill ta bort denna nyhet?</Text>
            </DialogContent>
            <DialogActions>
              <Button 
                appearance="secondary" 
                onClick={() => {
                  setDeleteConfirmOpen(false);
                  setNewsToDelete(null);
                }}
              >
                Avbryt
              </Button>
              <Button 
                appearance="primary" 
                onClick={async () => {
                  if (newsToDelete !== null) {
                    setDeleteConfirmOpen(false);
                    await onDeleteClick(newsToDelete);
                    setNewsToDelete(null);
                  }
                }}
              >
                Ta bort
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  );
};

export default NewsDrawer;
